import dagre from "dagre";
import { get } from "lodash";
import { Position } from "@xyflow/react";
import { hasPill } from "./edge-look";

const NODE_WIDTH = 232;
const NODE_HEIGHT = 60;
const RANK_GAP = 28;
const NODE_GAP = 80;
const PILL_SIZE = { width: 140, height: 40 };
const NEW_STEP_GAP = 28;
const MAX_NUDGES = 50;

export const LAYOUT_DIRECTIONS = { TOP_BOTTOM: "TB", LEFT_RIGHT: "LR" };

export const withHandles = (
  nodes = [],
  direction = LAYOUT_DIRECTIONS.TOP_BOTTOM,
) => {
  const isHorizontal = direction === LAYOUT_DIRECTIONS.LEFT_RIGHT;
  return nodes.map((node) => ({
    ...node,
    sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
    targetPosition: isHorizontal ? Position.Left : Position.Top,
  }));
};

export const layoutDirection = (nodes = []) => {
  if (nodes.length < 2) return LAYOUT_DIRECTIONS.TOP_BOTTOM;
  const xs = nodes.map((node) => get(node, "position.x") || 0);
  const ys = nodes.map((node) => get(node, "position.y") || 0);
  const width = Math.max(...xs) - Math.min(...xs);
  const height = Math.max(...ys) - Math.min(...ys);
  return width > height
    ? LAYOUT_DIRECTIONS.LEFT_RIGHT
    : LAYOUT_DIRECTIONS.TOP_BOTTOM;
};

export const applyLayout = (
  nodes = [],
  edges = [],
  direction = LAYOUT_DIRECTIONS.TOP_BOTTOM,
) => {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: direction, nodesep: NODE_GAP, ranksep: RANK_GAP });

  for (const node of nodes)
    graph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  for (const edge of edges)
    graph.setEdge(
      edge.source,
      edge.target,
      hasPill(edge, edges) ? { ...PILL_SIZE, labelpos: "c" } : {},
    );

  dagre.layout(graph);

  return withHandles(
    nodes.map((node) => {
      const { x, y } = graph.node(node.id);
      return {
        ...node,
        position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      };
    }),
    direction,
  );
};

const overlaps = (position, nodes) =>
  nodes.some(
    (node) =>
      Math.abs((get(node, "position.x") || 0) - position.x) < NODE_WIDTH &&
      Math.abs((get(node, "position.y") || 0) - position.y) < NODE_HEIGHT,
  );

const furthestNode = (nodes, isHorizontal) =>
  nodes.reduce((furthest, node) => {
    if (!furthest) return node;
    const axis = isHorizontal ? "position.x" : "position.y";
    return (get(node, axis) || 0) > (get(furthest, axis) || 0)
      ? node
      : furthest;
  }, null);

export const newStepPosition = (
  nodes = [],
  anchor = null,
  direction = LAYOUT_DIRECTIONS.TOP_BOTTOM,
) => {
  const isHorizontal = direction === LAYOUT_DIRECTIONS.LEFT_RIGHT;
  const base = anchor || furthestNode(nodes, isHorizontal);
  if (!base) return { x: 0, y: 0 };
  const x = get(base, "position.x") || 0;
  const y = get(base, "position.y") || 0;
  let position = isHorizontal
    ? { x: x + NODE_WIDTH + NEW_STEP_GAP, y }
    : { x, y: y + NODE_HEIGHT + NEW_STEP_GAP };
  for (let nudge = 0; nudge < MAX_NUDGES && overlaps(position, nodes); nudge++)
    position = isHorizontal
      ? { ...position, y: position.y + NODE_HEIGHT + NEW_STEP_GAP / 2 }
      : { ...position, x: position.x + NODE_WIDTH + NEW_STEP_GAP / 2 };
  return position;
};
